import {
    Component,
    Prop,
    h,
    Host,
    Watch,
    State,
    Listen,
    Element,
} from "@stencil/core";
import { Loader } from "@googlemaps/js-api-loader";
import { isCookieConsentAccepted } from "../../utils/cookieConsent";
// import { isPrivacyConsentAccepted } from "../../utils/privacyConsent";
import { locationDetailsStyles, dealerSearchStyles } from "./map-styles.js";
import {
    setCenter,
    locationDetailMap,
    baiduLocationDetailMap,
    baiduSetCenter,
} from "./utils/location-details";
import {
    dealerSearchMap,
    submitForm,
    changePostcode,
    toggleNoResultsMsg,
    setMarkers,
    clearMarkers,
    clearCountries,
    baiduDealerSearchMap,
} from "./utils/dealer-search";
import { fetchCountryLocationsData } from "./utils/country-locations";
import { LocationThumbnail } from "./location-thumbnail/location-thumbnail";

const baiduScriptId = "baidu-map";

@Component({
    tag: "ux-iws-location-map",
    styleUrl: "location-map.scss",
})
export class LocationMap {
    private baseClass = "ux-iws-location-map";
    private mapTarget: HTMLDivElement;
    private map: google.maps.Map = null;
    private maps: any = null;

    private formElement: HTMLFormElement = null;
    private submitForm = submitForm.bind(this);
    private changePostcode = changePostcode.bind(this);
    private toggleNoResultsMsg = toggleNoResultsMsg.bind(this);
    private setMarkers = setMarkers.bind(this);
    private fetchCountryLocationsData = fetchCountryLocationsData.bind(this);

    @Element() element: HTMLUxIwsLocationMapElement;

    //@ts-expect-error: used in utils function that access "this"
    private clearMarkers = clearMarkers.bind(this);
    //@ts-expect-error: used in utils function that access "this"
    private markers: any[] = [];
    //@ts-expect-error: used in utils function that access "this"
    private clearCountries = clearCountries.bind(this);
    //@ts-expect-error: used in utils function that access "this"
    private countries: any[] = [];
    //@ts-expect-error: used in utils function that access "this"
    private locationSlider: HTMLUxSliderElement = null;

    @State() postcode: string = null;
    @State() apiResponse: { location: any[] } = { location: [] };
    @State() sliderReady: boolean = false;
    @State() noResults: boolean = false;

    /** The type of the location map. */
    @Prop() type: "location-details" | "dealer-search" | "country-locations" =
        "location-details";
    /** The map provider to use. */
    @Prop() mapProvider: "google" | "baidu" = "google";
    /** The API key for the map provider. */
    @Prop() clientKey: string = null;
    /**
     * CSS selector of the button that leads the user to the route planner to the location.
     * It will be set to display: none if the cookies have not been accepted.
     */
    @Prop() routeLinkSelector: string = null;
    /** START: dealer search specific */
    @Prop() formSelector: string = null;
    /** CSS selector of the element that contains the no results message. */
    @Prop() noResultsCodeSelector: string = null;
    /** END: dealer search specific */

    @State() config: any = null;
    @State() cookiesReady: boolean = null;
    @State() privacyReady: boolean = null;
    @State() mapInitialized: boolean = false;

    private gMapsLoader = new Loader({
        apiKey: null,
        client: this.clientKey,
        version: "3",
    });

    componentWillLoad() {
        try {
            const tag: HTMLScriptElement = this.element.querySelector(
                `script[slot="config"][type="application/json"]`
            );
            if (tag === null) return;
            const content = tag.innerHTML;
            const parsedConfig = JSON.parse(content);
            this.config = parsedConfig;
        } catch {
            console.warn(
                "ux-iws-location-map: error while parsing map configurations"
            );
        }
        if (this.config !== null && this.type === "country-locations") {
            this.fetchCountryLocationsData({
                fetchUrl: this.config.fetchUrl,
            });
        }
    }

    async componentDidLoad() {
        isCookieConsentAccepted()
            .then(() => (this.cookiesReady = true))
            .catch(() => (this.cookiesReady = false));

        // TODO: change this when privacy consent is implemented
        this.privacyReady = true;
        // isPrivacyConsentAccepted()
        //     .then(() => (this.privacyReady = true))
        //     .catch(() => (this.privacyReady = false));
        if (this.type === "dealer-search") {
            this.toggleNoResultsMsg();
        }
    }

    connectedCallback() {
        if (this.type === "dealer-search") {
            this.formElement = document.querySelector(this.formSelector);
            this.formElement.addEventListener("submit", this.submitForm);
        }
    }

    disconnectedCallback() {
        if (this.type === "dealer-search") {
            this.formElement.removeEventListener("submit", this.submitForm);
        }
    }

    @Listen("uxCookieSettings", { target: "window" })
    handleCookiesAccepted(event) {
        const settings = event.detail;
        if (settings.optional === true) this.cookiesReady = true;
    }

    @Watch("privacyReady")
    @Watch("cookiesReady")
    initMap() {
        if (!this.cookiesReady) return;
        if (!this.privacyReady) return;

        if (this.mapProvider === "google") {
            this.initGoogleMaps();
        }
        if (this.mapProvider === "baidu") {
            this.initBaiduMaps();
        }
    }

    private initGoogleMaps() {
        this.gMapsLoader.load().then(({ maps }) => {
            this.maps = maps;

            if (this.type === "location-details") {
                this.map = locationDetailMap({
                    maps: this.maps,
                    config: this.config,
                    mapStyles: locationDetailsStyles,
                    mapTarget: this.mapTarget,
                    routeLinkSelector: this.routeLinkSelector,
                });
                setCenter(this.maps, this.map, this.config);
            }

            if (
                this.type === "dealer-search" ||
                this.type === "country-locations"
            ) {
                this.map = dealerSearchMap({
                    maps: this.maps,
                    mapStyles: dealerSearchStyles,
                    mapTarget: this.mapTarget,
                });
            }

            this.mapInitialized = true;
        });
    }

    private initBaiduMaps() {
        const script = document.createElement("script");
        script.src = `https://api.map.baidu.com/api?v=2.0&ak=${this.clientKey}&callback=initBaiduMap`;
        script.async = true;
        script.defer = true;
        script.type = "text/javascript";
        script.id = baiduScriptId;
        document.body.appendChild(script);

        window["initBaiduMap"] = async () => {
            this.maps = window["BMap"];
            if (this.type === "location-details") {
                this.map = await baiduLocationDetailMap({
                    maps: this.maps,
                    config: this.config,
                    mapTarget: this.mapTarget,
                    routeLinkSelector: this.routeLinkSelector,
                });
                baiduSetCenter(this.maps, this.map, this.config, 15);
            }

            if (
                this.type === "dealer-search" ||
                this.type === "country-locations"
            ) {
                this.map = baiduDealerSearchMap({
                    maps: this.maps,
                    mapTarget: this.mapTarget,
                });
                baiduSetCenter(
                    this.maps,
                    this.map,
                    {
                        lng: "99",
                        lat: "33",
                    },
                    1
                );
            }

            this.mapInitialized = true;
        };
    }

    @Watch("apiResponse")
    @Watch("mapInitialized")
    updateSlider() {
        this.sliderReady = true;
        if (this.type === "dealer-search" && this.mapInitialized) {
            this.setMarkers({
                mapProvider: this.mapProvider,
                withCountries: false,
            });
        }
        if (this.type === "country-locations" && this.mapInitialized) {
            this.setMarkers({
                mapProvider: this.mapProvider,
                withCountries: true,
            });
        }
    }

    @Watch("postcode")
    updatePostcode() {
        this.changePostcode();
    }

    @Watch("noResults")
    updateNoResultsMessage() {
        this.toggleNoResultsMsg();
    }

    @Listen("resize", { target: "window" })
    resetCenterOnResize() {
        if (this.mapInitialized && this.type === "location-details") {
            if (this.mapProvider === "google") {
                setCenter(this.maps, this.map, this.config);
            } else {
                baiduSetCenter(this.maps, this.map, this.config, 15);
            }
        }
    }

    render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--${this.type}`]: true,
                }}
            >
                {!this.mapInitialized && (
                    <style>
                        {`
                            ${this.routeLinkSelector} {
                                display: none!important;
                            }
                        `}
                    </style>
                )}
                <div class={`${this.baseClass}__map-container`}>
                    <div
                        class={`${this.baseClass}__map-target`}
                        ref={(el) => (this.mapTarget = el)}
                    >
                        <div
                            class={{
                                [`${this.baseClass}__cookie-notice`]: true,
                                ["ux-u-background-grey"]: true,
                                [`${this.baseClass}__cookie-notice--hidden`]:
                                    this.cookiesReady ||
                                    this.privacyReady === false, // if both are not accepted, only show privacy notice
                                [`${this.baseClass}__cookie-notice--standby`]:
                                    this.cookiesReady === null,
                            }}
                        >
                            <slot name="cookie-notice" />
                        </div>
                        <div
                            class={{
                                [`${this.baseClass}__privacy-notice`]: true,
                                ["ux-u-background-grey"]: true,
                                [`${this.baseClass}__privacy-notice--hidden`]:
                                    this.privacyReady,
                                [`${this.baseClass}__privacy-notice--standby`]:
                                    this.privacyReady === null,
                            }}
                        >
                            <slot name="privacy-notice" />
                        </div>
                    </div>
                    <slot name="location-details" />
                    {(this.type === "dealer-search" ||
                        this.type === "country-locations") && (
                        <div class={`${this.baseClass}__slider-container`}>
                            {this.sliderReady && (
                                <ux-slider
                                    ref={(el) => (this.locationSlider = el)}
                                    direction="vertical"
                                    breakpoint-s="767"
                                    breakpoint-m="1023"
                                    breakpoint-l="1439"
                                    visible-slides-s="auto"
                                    visible-slides-m="auto"
                                    visible-slides-l="auto"
                                    visible-slides-xl="auto"
                                    no-drag
                                >
                                    {this.apiResponse.location?.map(
                                        (location) => {
                                            return (
                                                <ux-slider-slide
                                                    key={location.name}
                                                    style={{ height: "auto" }}
                                                >
                                                    <LocationThumbnail
                                                        name={location.name}
                                                        street1={
                                                            location.street_1
                                                        }
                                                        street2={
                                                            location.street_2
                                                        }
                                                        imageType={
                                                            this.type ===
                                                            "country-locations"
                                                                ? this.config?.[
                                                                      location.type +
                                                                          "_icon"
                                                                  ]
                                                                : null
                                                        }
                                                        link={location.link}
                                                        postcode={location.plz}
                                                        city={location.city}
                                                        tel={
                                                            this.type ===
                                                            "country-locations"
                                                                ? null
                                                                : location.tel ||
                                                                  null
                                                        }
                                                        email={
                                                            this.type ===
                                                            "country-locations"
                                                                ? null
                                                                : location.email ||
                                                                  null
                                                        }
                                                        baseClass={
                                                            this.baseClass
                                                        }
                                                    />
                                                </ux-slider-slide>
                                            );
                                        }
                                    )}
                                </ux-slider>
                            )}
                        </div>
                    )}
                </div>
                <div style={{ display: "none" }}>
                    <slot name="config"></slot>
                </div>
            </Host>
        );
    }
}
